import { Menu, NullIcon, Tooltip } from "@repo/ui/core";
import {
  IconBuilding,
  IconChevronDown,
  IconCode,
  IconDevices,
  IconHome,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconServer2,
  IconStack2,
  IconTerminal2,
  IconUserCircle,
  type Icon,
} from "@repo/ui/icons";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { NavSection } from "../nav";
import classes from "./shell.module.scss";

export const NAV_ICONS: Record<string, Icon> = {
  home: IconHome,
  devices: IconDevices,
  code: IconCode,
  "stack-2": IconStack2,
  building: IconBuilding,
  "server-2": IconServer2,
  terminal: IconTerminal2,
  "user-circle": IconUserCircle,
};

type Props = {
  sections: NavSection[];
  bottom: NavSection[];
  collapsed: boolean;
  onToggle: () => void;
};

export function Sidebar({ sections, bottom, collapsed, onToggle }: Props) {
  const { pathname } = useLocation();
  // Pinned sections stay open alongside the active one; navigating clears them.
  const [pins, setPins] = useState<Record<string, boolean>>({});
  useEffect(() => setPins({}), [pathname]);

  const render = (list: NavSection[]) =>
    list.map((s) => (
      <NavEntry
        key={s.key}
        section={s}
        collapsed={collapsed}
        open={s.subs.length > 0 && (pins[s.key] ?? s.active)}
        onToggleOpen={(open) =>
          setPins((p) => {
            // keep the active section's current state when pinning another
            const active = [...sections, ...bottom].find((x) => x.active && x.key !== s.key);
            return { ...(active ? { [active.key]: p[active.key] ?? true } : {}), ...p, [s.key]: open };
          })
        }
      />
    ));

  return (
    <nav aria-label="Primary" className={classes.nav} data-collapsed={collapsed || undefined}>
      <div className={classes.logo}>
        <Link to="/" aria-label="Null Autos home" className={classes.brand}>
          <NullIcon w={24} h={19} />
          {!collapsed && <span>null.autos</span>}
        </Link>
      </div>
      <div className={classes.navList}>{render(sections)}</div>
      <div className={classes.navBottom}>{render(bottom)}</div>
      <div className={classes.collapse}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <IconLayoutSidebarLeftExpand size={18} /> : <IconLayoutSidebarLeftCollapse size={18} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  );
}

function NavEntry({
  section: s,
  collapsed,
  open,
  onToggleOpen,
}: {
  section: NavSection;
  collapsed: boolean;
  open: boolean;
  onToggleOpen: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const Icon = NAV_ICONS[s.icon] ?? IconHome;
  const hasSubs = s.subs.length > 0;
  // Fill the parent only when no child row carries the highlight.
  const filled = s.active && !(open && !collapsed && s.subs.some((c) => c.active));

  const item = (
    <button
      type="button"
      className={classes.navItem}
      data-active={s.active || undefined}
      data-filled={filled || undefined}
      aria-label={s.label}
      aria-current={s.active && !hasSubs ? "page" : undefined}
      aria-expanded={hasSubs ? open : undefined}
      onClick={collapsed && hasSubs ? undefined : () => navigate(s.to)}
    >
      <Icon size={18} />
      {!collapsed && (
        <>
          <span className={classes.navLabel}>{s.label}</span>
          {s.badge && (
            <span className={classes.badge} data-tone={s.badgeTone} style={{ marginRight: hasSubs ? 22 : 0 }}>
              {s.badge}
            </span>
          )}
        </>
      )}
      {collapsed && s.badge && s.badgeTone && (
        <span className={classes.badgeDot} style={{ color: `var(--na-${s.badgeTone})` }} />
      )}
    </button>
  );

  if (collapsed) {
    if (!hasSubs) {
      return (
        <Tooltip label={s.label} position="right">
          <div className={classes.navRow}>{item}</div>
        </Tooltip>
      );
    }
    return (
      <Menu position="right-start" offset={10} width={220} withinPortal>
        <Menu.Target>
          <div className={classes.navRow}>{item}</div>
        </Menu.Target>
        <Menu.Dropdown>
          <div className={classes.flyTitle}>{s.label}</div>
          {s.subs.map((c) => (
            <Menu.Item
              key={c.key}
              onClick={() => navigate(c.to)}
              rightSection={
                c.badge ? (
                  <span className={classes.badge} data-tone={c.badgeTone}>
                    {c.badge}
                  </span>
                ) : undefined
              }
              style={c.active ? { background: "var(--na-surface-hover)" } : undefined}
              fz={12}
            >
              {c.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div className={classes.navRow}>
        {item}
        {hasSubs && (
          <button
            type="button"
            className={classes.chevron}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${s.label}`}
            title={`${open ? "Collapse" : "Expand"} ${s.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleOpen(!open);
            }}
          >
            <IconChevronDown size={14} />
          </button>
        )}
      </div>
      {open && (
        <div role="group" aria-label={s.label} className={classes.subs}>
          {s.subs.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              className={classes.sub}
              data-active={c.active || undefined}
              aria-current={c.active ? "page" : undefined}
            >
              <span className={classes.navLabel}>{c.label}</span>
              {c.badge && (
                <span className={classes.badge} data-tone={c.badgeTone}>
                  {c.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
